import { FOUNDATIONS, PALETTE } from '@overlayqa/shared';
import { useState, useRef, useCallback, useEffect, useLayoutEffect } from 'react';
import { createPortal } from 'react-dom';

/** Space kept between a bubble and the screen edge. */
const VIEWPORT_MARGIN = 8;

/**
 * How far to slide a bubble sideways so it stays on screen (2026-11-01: the
 * branding lock's 320px bubble ran 50px past a 390px phone). `left` is the
 * anchor the placement computed; the bubble spans [left - width/2, left +
 * width/2] for top and bottom, [left - width, left] for top-end and left, and
 * [left, left + width] for right. 0 when it already fits.
 */
function viewportShift(placement: TooltipProps['placement'], left: number, width: number, viewportWidth: number): number {
  const start = placement === 'top-end' || placement === 'left' ? left - width
    : placement === 'right' ? left : left - width / 2;
  const end = start + width;
  if (end > viewportWidth - VIEWPORT_MARGIN) return Math.max(VIEWPORT_MARGIN - start, viewportWidth - VIEWPORT_MARGIN - end);
  if (start < VIEWPORT_MARGIN) return VIEWPORT_MARGIN - start;
  return 0;
}

interface TooltipProps {
  label: string;
  children: React.ReactElement;
  /**
   * 'top-end' pins the bubble's RIGHT edge to the trigger's right edge and
   * grows leftward, for a trigger near the right of the viewport (a centred
   * 'top' bubble would run off screen there).
   */
  placement?: 'top' | 'top-end' | 'right' | 'bottom' | 'left';
  delay?: number;
  /** Wrap a long label (up to 320px wide) instead of cutting it to one 240px line. */
  multiline?: boolean;
}

/**
 * Dark speech-bubble tooltip: near-black (#1b1c1d) rounded bubble, bold white
 * text, and a solid caret pointing at the trigger. Renders on hover/focus
 * after a short delay.
 */
export function Tooltip({ label, children, placement = 'right', delay = 400, multiline = false }: TooltipProps) {
  const [visible, setVisible] = useState(false);
  const [coords, setCoords] = useState({ top: 0, left: 0, triggerWidth: 0 });
  const [shift, setShift] = useState(0);
  const triggerRef = useRef<HTMLElement | null>(null);
  const bubbleRef = useRef<HTMLDivElement | null>(null);
  const timeoutRef = useRef<ReturnType<typeof setTimeout>>();

  const show = useCallback(() => {
    // Hover and focus can both fire before the delay; keep one pending timer
    // so leaving the trigger cancels every scheduled appearance.
    clearTimeout(timeoutRef.current);
    timeoutRef.current = setTimeout(() => setVisible(true), delay);
  }, [delay]);

  const hide = useCallback(() => {
    clearTimeout(timeoutRef.current);
    setVisible(false);
  }, []);

  useLayoutEffect(() => {
    if (!visible || !triggerRef.current) return;
    // display:contents elements have a zero-size bounding rect — measure the
    // first child element instead so the tooltip positions correctly.
    const el = triggerRef.current.firstElementChild as HTMLElement | null;
    const rect = (el ?? triggerRef.current).getBoundingClientRect();
    const gap = 10;

    switch (placement) {
      case 'right':
        setCoords({ top: rect.top + rect.height / 2, left: rect.right + gap, triggerWidth: rect.width });
        break;
      case 'left':
        setCoords({ top: rect.top + rect.height / 2, left: rect.left - gap, triggerWidth: rect.width });
        break;
      case 'top':
        setCoords({ top: rect.top - gap, left: rect.left + rect.width / 2, triggerWidth: rect.width });
        break;
      case 'top-end':
        setCoords({ top: rect.top - gap, left: rect.right, triggerWidth: rect.width });
        break;
      case 'bottom':
        setCoords({ top: rect.bottom + gap, left: rect.left + rect.width / 2, triggerWidth: rect.width });
        break;
    }
  }, [visible, placement]);

  // Measured after the bubble renders at its anchor and before paint, so a
  // bubble that would run off screen never shows there.
  useLayoutEffect(() => {
    if (!visible || !bubbleRef.current) { setShift(0); return; }
    setShift(viewportShift(placement, coords.left, bubbleRef.current.getBoundingClientRect().width, window.innerWidth));
  }, [visible, placement, coords.left]);

  useEffect(() => {
    return () => clearTimeout(timeoutRef.current);
  }, []);

  const transform =
    placement === 'right' ? 'translateY(-50%)'
    : placement === 'left' ? 'translate(-100%, -50%)'
    : placement === 'top' ? 'translate(-50%, -100%)'
    : placement === 'top-end' ? 'translate(-100%, -100%)'
    : 'translateX(-50%)';

  // The caret moves opposite to the bubble, so it keeps pointing at the trigger.
  const caretLeft = shift === 0 ? '50%' : `calc(50% ${shift > 0 ? '-' : '+'} ${Math.abs(shift)}px)`;
  const arrowStyle: React.CSSProperties =
    placement === 'right' ? { left: -8, top: '50%', transform: 'translateY(-50%)', borderRight: "8px solid " + PALETTE.surface.dark, borderTop: '8px solid transparent', borderBottom: '8px solid transparent', borderLeft: 'none' }
    : placement === 'left' ? { right: -8, top: '50%', transform: 'translateY(-50%)', borderLeft: "8px solid " + PALETTE.surface.dark, borderTop: '8px solid transparent', borderBottom: '8px solid transparent', borderRight: 'none' }
    : placement === 'top' ? { bottom: -8, left: caretLeft, transform: 'translateX(-50%)', borderTop: "8px solid " + PALETTE.surface.dark, borderLeft: '8px solid transparent', borderRight: '8px solid transparent', borderBottom: 'none' }
    // Caret under the trigger's centre, kept clear of the bubble's 12px corner.
    : placement === 'top-end' ? { bottom: -8, right: Math.max(12, coords.triggerWidth / 2 - 8 + shift), borderTop: "8px solid " + PALETTE.surface.dark, borderLeft: '8px solid transparent', borderRight: '8px solid transparent', borderBottom: 'none' }
    : { top: -8, left: caretLeft, transform: 'translateX(-50%)', borderBottom: "8px solid " + PALETTE.surface.dark, borderLeft: '8px solid transparent', borderRight: '8px solid transparent', borderTop: 'none' };

  return (
    <>
      {/* Clone child to attach ref + event handlers */}
      <span
        ref={triggerRef as React.RefObject<HTMLSpanElement>}
        onMouseEnter={show}
        onMouseLeave={hide}
        onFocus={show}
        onBlur={hide}
        style={{ display: 'contents' }}
      >
        {children}
      </span>

      {visible && createPortal(
        <div
          ref={bubbleRef}
          role="tooltip"
          style={{
            position: 'fixed',
            zIndex: 9999,
            top: coords.top,
            left: coords.left + shift,
            transform,
            maxWidth: multiline ? 320 : 240,
            padding: `${FOUNDATIONS.space[2]} ${FOUNDATIONS.space[4]}`,
            background: PALETTE.surface.dark,
            color: PALETTE.surface.card,
            fontSize: FOUNDATIONS.fontSize.body,
            fontWeight: FOUNDATIONS.fontWeight.bold,
            lineHeight: FOUNDATIONS.lineHeight.label,
            borderRadius: FOUNDATIONS.radius.dialog,
            boxShadow: '0 6px 16px rgba(0,0,0,0.28)',
            pointerEvents: 'none',
            ...(multiline
              // width: max-content is load-bearing. A fixed box is otherwise sized
              // against the space to its RIGHT, so near the viewport edge the label
              // wrapped one word per line (measured 53px wide x 584px tall).
              ? { whiteSpace: 'normal' as const, overflowWrap: 'anywhere' as const, width: 'max-content' }
              : { whiteSpace: 'nowrap' as const, overflow: 'hidden', textOverflow: 'ellipsis' }),
          }}
        >
          {label}
          <span style={{ position: 'absolute', width: 0, height: 0, ...arrowStyle }} />
        </div>,
        document.body,
      )}
    </>
  );
}
